import { ArrowRightIcon, LoaderCircleIcon } from 'lucide-react'

import { Button, ButtonGroup, type IconProp, Tooltip, useBreakpoint } from 'slab'

import useAppNavigation from '../hooks/useAppNavigation'

type Props = {
  backButtonText?: string
  nextButtonText?: string
  nextButtonIcon?: IconProp
  nextButtonTooltipText?: string
  onBack?: () => void
  onNext?: () => void
  isNextDisabled?: boolean
  isLoading?: boolean
  tertiaryButtonText?: string
  onClickTertiary?: () => void
  hideBackButton?: boolean
  onNextButtonMouseEnter?: () => void
  onNextButtonMouseLeave?: () => void
}

const LoadingIndicator = () => {
  return (
    <LoaderCircleIcon className='animate-spin' />
  )
}

export default function NavigationButtons ({
  backButtonText = 'Back',
  nextButtonText = 'Next',
  nextButtonIcon = ArrowRightIcon,
  nextButtonTooltipText,
  onBack,
  onNext,
  isNextDisabled,
  isLoading,
  tertiaryButtonText,
  onClickTertiary,
  hideBackButton = false,
  onNextButtonMouseEnter,
  onNextButtonMouseLeave,
}: Props) {
  const { navigateBack } = useAppNavigation()
  const { breakpointIsAtLeast } = useBreakpoint()

  const onClickBack = () => {
    if (onBack) {
      onBack()
    } else {
      navigateBack()
    }
  }

  const nextButton = onNext && (
    <div onMouseEnter={onNextButtonMouseEnter} onMouseLeave={onNextButtonMouseLeave}>
      <Tooltip content={nextButtonTooltipText} enabled={!!nextButtonTooltipText}>
        <Button
          variant='primary'
          onClick={onNext}
          disabled={isNextDisabled || isLoading}
          rightIcon={isLoading ? LoadingIndicator : nextButtonIcon}
          onMouseEnter={onNextButtonMouseEnter}
          onMouseLeave={onNextButtonMouseLeave}
        >
          {nextButtonText}
        </Button>
      </Tooltip>
    </div>
  )

  const backButton = !hideBackButton && (
    <Button
      variant='secondary'
      onClick={onClickBack}
      disabled={isLoading}
    >
      {backButtonText}
    </Button>
  )

  const tertiaryButton = tertiaryButtonText && (
    <Button
      variant='tertiary-deemphasized'
      onClick={onClickTertiary}
      disabled={isLoading}
    >
      {tertiaryButtonText}
    </Button>
  )

  if (breakpointIsAtLeast('xs')) {
    return (
      <ButtonGroup size='large' align='center'>
        {tertiaryButton}
        {backButton}
        {nextButton}
      </ButtonGroup>
    )
  }

  return (
    <ButtonGroup size='large' layout='vertical'>
      {nextButton}
      {backButton}
      {tertiaryButton}
    </ButtonGroup>
  )
}
